'use client';

import { FC, SVGProps } from 'react';
import { SvgIconComponent } from '@mui/icons-material';
import { Box, SvgIcon, SxProps, Theme } from '@mui/material';
import WarningIcon from '/public/icons/warningIcon.svg?icon';

interface IconWithTextProps {
  title: string | React.ReactNode;
  icon?: SvgIconComponent | FC<SVGProps<SVGElement>>;
  description?: string | React.ReactNode;
  fontWeight?: number;
  iconColor?: string;
  textColor?: string;
  boxSx?: SxProps<Theme>;
  iconSx?: SxProps<Theme>;
  descriptionSx?: SxProps<Theme>;
  typographySx?: SxProps<Theme>;
  viewBox?: string;
  footerViewBox?: string;
  iconWrapperSx?: SxProps<Theme>;
  handleClick?: () => void;
}

function IsMuiIcon(
  icon: SvgIconComponent | FC<SVGProps<SVGElement>>,
): icon is SvgIconComponent {
  return 'muiName' in icon;
}

const IconWithText: React.FC<IconWithTextProps> = ({
  icon = WarningIcon,
  title,
  boxSx,
  iconSx,
  description,
  typographySx,
  viewBox,
  iconWrapperSx,
  descriptionSx,
  handleClick,
}) => {
  const initBoxSx: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '16px',
    padding: '24px',
    gap: 1,
    alignItems: 'flex-start',
    border: '1px solid inherited',
  };

  const initIconSx: SxProps<Theme> = {
    fontSize: '16px',
    color: 'inherit',
    fill: IsMuiIcon(icon) ? 'currentColor' : 'none',
  };

  const initTypographySx: SxProps<Theme> = {
    fontSize: '20px',
    fontWeight: '500',
    color: 'inherit',
    lineHeight: 'normal',
  };

  return (
    <Box sx={{ ...initBoxSx, ...boxSx }} onClick={handleClick}>
      <Box sx={{ ...iconWrapperSx }}>
        <SvgIcon
          component={icon}
          inheritViewBox={!viewBox}
          viewBox={viewBox}
          sx={{
            ...initIconSx,
            ...iconSx,
          }}
        />
      </Box>
      <Box
        sx={{
          ...initTypographySx,
          ...typographySx,
        }}
      >
        {title}
      </Box>
      <Box
        sx={{
          ...descriptionSx,
        }}
      >
        {description}
      </Box>
    </Box>
  );
};

export default IconWithText;
